import React from 'react'
import { styled, keyframes } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import {
  Bell,
  Home as HomeIcon,
  Wallet,
  TrendingUp,
  PiggyBank,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import BoltIcon from '@mui/icons-material/Bolt'
import SecurityIcon from '@mui/icons-material/Security'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import LanguageIcon from '@mui/icons-material/Language'
import SvgIcon from '@mui/material/SvgIcon'
import AppleIcon from '@mui/icons-material/Apple'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import SendIcon from '@mui/icons-material/Send'

// Styled Components
const pulse = keyframes`
  0% {
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(0.95);
  }
  50% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1.05);
  }
  100% {
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(0.95);
  }
`

const floatY = keyframes`
  0% {
    top: 30%;
  }
  50% {
    top: 40%;
  }
  100% {
    top: 30%;
  }
`

const shadowPulse = keyframes`
  0% {
    box-shadow: 0 20px 50px #122425;
  }
  50% {
    box-shadow: 0 20px 90px rgba(18, 36, 37, 0.8);
  }
  100% {
    box-shadow: 0 20px 50px #122425;
  }
`

const GooglePlayIcon = (props) => (
  <SvgIcon {...props}>
    <path d='M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z' />
  </SvgIcon>
)

const Section = styled('section')(({ theme }) => ({
  backgroundColor: '#0E1116',
  padding: '80px 0',
  color: '#fff',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: '60px 0px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 10px',
  },
}))

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  color: '#C1C8D3',
  fontSize: '0.95rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    gap: '8px',
    marginBottom: '12px',
  },
  '& svg': {
    color: '#21AB9D',
    backgroundColor: '#132E2F',
    borderRadius: '3px',
    padding: '4px',
    fontSize: '1.5rem',
  },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '3rem',
  lineHeight: 1.2,
  marginBottom: '24px',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
    marginBottom: '16px',
  },
}))

const SectionDescription = styled(Typography)(({ theme }) => ({
  color: '#9AA6B2',
  fontSize: '1rem',
  // marginBottom: '40px',
  maxWidth: '500px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    marginBottom: '24px',
  },
}))

const AppButton = styled(Button)(({ variant, theme }) => ({
  textTransform: 'none',
  padding: '12px 24px',
  borderRadius: '12px',
  fontSize: '1rem',
  fontWeight: 600,
  marginRight: '16px',
  marginBottom: '16px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: variant === 'contained' ? '#1B9F93' : 'transparent',
  color: variant === 'contained' ? '#fff' : '#fff',
  border: variant === 'outlined' ? '1px solid #23262F' : 'none',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: '10px 20px',
    marginRight: '12px',
    marginBottom: '12px',
    gap: '6px',
  },
  '&:hover': {
    backgroundColor: variant === 'contained' ? '#1B9F93' : '#21242C',
    boxShadow: variant === 'contained' ? '0 0 10px #21AB9D' : 'none',
    borderColor: variant === 'outlined' ? '#21AB9D' : 'transparent',
  },
}))

// Phone Mockup Components
const PhoneFrame = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '320px',
  height: '660px',
  background: '#000',
  borderRadius: '40px',
  border: '8px solid #23262F',
  padding: '15px',
  position: 'relative',
  margin: '0 auto',
  boxShadow: '0 20px 50px #122425',
  animation: `${shadowPulse} 4s ease-in-out infinite`,
  display: 'flex',
  flexDirection: 'column',
  transform: 'rotate(2deg)',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'rotate(0deg)',
  },
  [theme.breakpoints.down('md')]: {
    transform: 'none',
    height: '650px',
    marginTop: '40px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '280px',
    height: '650px',
    border: '6px solid #23262F',
    padding: '16px',
    borderRadius: '30px',
    marginTop: '32px',
  },
}))

const PhoneScreen = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
})

const StatusBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '12px',
  fontWeight: 600,
  marginBottom: '10px',
  color: '#fff',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '16px',
  },
}))

const BalanceCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#0F1F21',
  borderRadius: '20px',
  padding: '20px',
  marginBottom: '14px',
  border: '1px solid rgba(29, 176, 161, 0.15)',
  // background: '#0F1F21',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    marginBottom: '14px',
  },
}))

const ActionButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  color: '#9AA6B2',
  fontSize: '10px',
  cursor: 'pointer',
  '& .icon-circle': {
    width: '36px',
    height: '36px',
    borderRadius: '12px',
    background:
      'linear-gradient(180deg, rgba(29,175,161,0.20) 0%, rgba(29,175,161,0.05) 100%)',
    border: '1px solid rgba(29,175,161,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    transition: 'all 0.2s',
  },
  '&:hover .icon-circle': {
    background:
      'linear-gradient(180deg, rgba(29,175,161,0.28) 0%, rgba(29,175,161,0.10) 100%)',
    border: '1px solid rgba(29,175,161,0.30)',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '4px',
    '& .icon-circle': {
      width: '32px',
      height: '32px',
    },
  },
}))

const CryptoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px ',
  backgroundColor: '#101318',
  marginBottom: '8px',
  borderRadius: '12px',
  [theme.breakpoints.down('sm')]: {
    padding: '8px',
    marginBottom: '6px',
  },
}))

const CoinIcon = styled(Box)(({ color, theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  color: '#fff',
  fontSize: '14px',
  [theme.breakpoints.down('sm')]: {
    width: '32px',
    height: '32px',
    fontSize: '12px',
  },
}))

const cryptoData = [
  {
    symbol: 'BTC',
    name: 'Bitcoin',
    price: '43,210.25',
    change: '+2.34',
    positive: true,
    icon: '₿',
  },
  {
    symbol: 'ETH',
    name: 'Ethereum',
    price: '2,310.80',
    change: '+1.12',
    positive: true,
    icon: 'Ξ',
  },
  {
    symbol: 'SOL',
    name: 'Solana',
    price: '98.20',
    change: '-0.84',
    positive: false,
    icon: '◎',
  },
  {
    symbol: 'USDT',
    name: 'Tether',
    price: '1.00',
    change: '+0.01',
    positive: true,
    icon: '₮',
  },
]

const bottomNavItems = [
  { icon: HomeIcon, label: 'Home', active: true },
  { icon: TrendingUp, label: 'Trade', active: false },
  { icon: PiggyBank, label: 'Earn', active: false },
  { icon: Wallet, label: 'Wallet', active: false },
]

function TradeAnywhere() {
  return (
    <Section>
      <div className='container'>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems='center'>
          {/* Left Content */}
          <Grid item xs={12} lg={6}>
            <SectionTitle variant='h2'>
              Trade{' '}
              <span
                style={{
                  display: 'inline-block',
                  backgroundImage:
                    'linear-gradient(to right, #21AB9D, #34d399)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Anywhere.
              </span>
              <br />
              Anytime. On Any Device.
            </SectionTitle>
            <SectionDescription variant='body1'>
              Experience seamless trading on the go with our powerful mobile
              applications. Never miss a market opportunity.
            </SectionDescription>

            <Box sx={{ my: 5 }}>
              <FeatureItem>
                <SmartphoneIcon />
                iOS & Android apps
              </FeatureItem>
              <FeatureItem>
                <BoltIcon />
                Ultra-fast order execution
              </FeatureItem>
              <FeatureItem>
                <SecurityIcon />
                Advanced security & encryption
              </FeatureItem>
              <FeatureItem>
                <NotificationsActiveIcon />
                Real-time price alerts
              </FeatureItem>
              <FeatureItem>
                <LanguageIcon />
                Global access, 24/7 markets
              </FeatureItem>
            </Box>

            <Box>
              <AppButton
                variant='contained'
                component='a'
                href='https://play.google.com/store/apps/details?id=com.app.unitic&hl=en-IN/'
                target='_blank'
                rel='noopener noreferrer'
              >
                <GooglePlayIcon /> Google Play
              </AppButton>
              <AppButton
                variant='outlined'
                onClick={() =>
                  window.open('/comingsoon', '_blank', 'noopener,noreferrer')
                }
              >
                <AppleIcon sx={{ color: 'white' }} /> App Store
              </AppButton>
            </Box>
          </Grid>

          {/* Right Content - Phone Mockup */}
          <Grid item xs={12} lg={6} sx={{ position: 'relative' }}>
            <PhoneFrame>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(29,175,161,0.2)',
                  filter: 'blur(30px)',
                  borderRadius: '50%',
                  animation: `${pulse} 4s ease-in-out infinite`,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '30%',
                  left: '-25px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(247, 147, 26, 0.4)',
                  display: { xs: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  zIndex: 1,
                  animation: `${floatY} 6s ease-in-out infinite`,
                }}
              >
                ₿
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: '-25px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(98, 126, 234, 0.4)',
                  display: { xs: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  zIndex: 1,
                  animation: `${floatY} 6s ease-in-out infinite`,
                }}
              >
                Ξ
              </Box>
              <PhoneScreen>
                <StatusBar>
                  <span>9:41</span>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 10,
                        border: '1px solid #fff',
                        borderRadius: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 8,
                          borderRadius: 0.3,
                          borderTopRightRadius: '3px',
                          borderBottomRightRadius: '3px',
                          backgroundColor: '#1DAFA1',
                        }}
                      />
                    </Box>
                  </Box>
                </StatusBar>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: '#9AA6B2',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Welcome back
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 800,
                        background:
                          'linear-gradient(90deg, #21AB9D 0%, #34d399 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      My Portfolio
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '10px',
                      background:
                        'linear-gradient(135deg, rgba(29,175,161,0.3) 0%, rgba(29,175,161,0.1) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(29,175,161,0.3)',
                    }}
                  >
                    <Bell size={14} color='#1DAFA1' />
                  </Box>
                </Box>

                <BalanceCard>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -16,
                      right: -16,
                      width: 96,
                      height: 96,
                      backgroundColor: 'rgba(29,175,161,0.15)',
                      borderRadius: '50%',
                      filter: 'blur(18px)',
                    }}
                  />
                  <Typography variant='body2' sx={{ color: '#9AA6B2', mb: 1 }}>
                    Total Balance
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 'bold', fontSize: '1.2rem', mb: 1 }}
                  >
                    $148,650.00
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'rgba(34,197,94,0.15)',
                        color: '#21CA50',
                        px: 1,
                        py: 0.3,
                        borderRadius: '999px',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      <ArrowUpRight size={14} />
                      +$4,240.50
                    </Box>
                    <Typography variant='caption' sx={{ color: '#9AA6B2' }}>
                      (+2.8%) today
                    </Typography>
                  </Box>
                </BalanceCard>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2,
                    px: 1,
                  }}
                >
                  <ActionButton>
                    <div className='icon-circle'>
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    </div>
                    Deposit
                  </ActionButton>
                  <ActionButton>
                    <div className='icon-circle'>
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    </div>
                    Withdraw
                  </ActionButton>
                  <ActionButton>
                    <div className='icon-circle'>
                      <SwapHorizIcon sx={{ fontSize: 16 }} />
                    </div>
                    Swap
                  </ActionButton>
                  <ActionButton>
                    <div className='icon-circle'>✦</div>
                    Earn
                  </ActionButton>
                </Box>

                <Box
                  sx={{
                    mb: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 800, color: '#e6edf3' }}
                  >
                    Top Markets
                  </Typography>
                  <Typography
                    sx={{ fontSize: 10, color: '#1DAFA1', fontWeight: 600 }}
                  >
                    See all →
                  </Typography>
                </Box>

                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    pr: 0.5,
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {cryptoData.map((c) => (
                    <CryptoItem key={c.symbol}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <CoinIcon color='rgba(29,175,161,0.2)'>
                          {c.icon}
                        </CoinIcon>
                        <Box>
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {c.symbol}
                          </Typography>
                          <Typography
                            variant='caption'
                            sx={{ color: '#9AA6B2' }}
                          >
                            {c.name}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.8rem' }}>
                          ${c.price}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'flex-end',
                            fontSize: 10,
                            fontWeight: 700,
                            color: c.positive ? '#34D399' : '#F87171',
                          }}
                        >
                          {c.positive ? (
                            <ArrowUpRight size={10} />
                          ) : (
                            <ArrowDownRight size={10} />
                          )}
                          {c.change}%
                        </Box>
                      </Box>
                    </CryptoItem>
                  ))}
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 5,
                    px: 2,
                    pb: 0.5,
                    borderBottomRightRadius: '10px',
                    borderBottomLeftRadius: '10px',
                    background:
                      'linear-gradient(0deg, rgba(20,22,26,0.9) 0%, rgba(20,22,26,0.6) 100%)',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-around',
                    }}
                  >
                    {bottomNavItems.map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexDirection: 'column',
                          gap: 0.5,
                          color: item.active ? '#1DAFA1' : '#9AA6B2',
                        }}
                      >
                        <Box
                          sx={{
                            // p: 0.8,
                            borderRadius: '10px',
                            // background: item.active
                            //   ? 'rgba(29,175,161,0.2)'
                            //   : 'transparent',
                            // boxShadow: item.active
                            //   ? '0 0 8px rgba(29,175,161,0.3)'
                            //   : 'none',
                          }}
                        >
                          <item.icon size={16} />
                        </Box>
                        <Typography sx={{ fontSize: 9, fontWeight: 700 }}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </PhoneScreen>
            </PhoneFrame>
          </Grid>
        </Grid>
      </div>
    </Section>
  )
}

export default TradeAnywhere
